/**
 * ChatInput - Text input component for sending chat messages
 */

import React, { FormEvent, KeyboardEvent, useCallback, useRef, useState } from 'react'

import { RATE_LIMITS, UI } from '../lib/constants'
import { createRateLimiter } from '../lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  className?: string
  showSendButton?: boolean
  autoFocus?: boolean
  onTyping?: (isTyping: boolean) => void
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  placeholder = 'Type a message...',
  disabled = false,
  maxLength = UI.CHAT_INPUT_MAX_LENGTH,
  className = '',
  autoFocus = false,
  onTyping,
}) => {
  const [message, setMessage] = useState('')
  const [isComposing, setIsComposing] = useState(false)
  const [showError, setShowError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Rate limiter for messages
  const rateLimiter = useRef(createRateLimiter(RATE_LIMITS.CHAT_MESSAGES_PER_MINUTE, 60000))

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      // Enforce max length
      if (value.length <= maxLength) {
        setMessage(value)
        setShowError(false)

        // Notify typing status
        if (onTyping) {
          onTyping(true)

          // Clear existing timeout
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
          }

          // Set timeout to stop typing indicator
          typingTimeoutRef.current = setTimeout(() => {
            onTyping(false)
          }, 1000)
        }
      }
    },
    [maxLength, onTyping],
  )

  // Handle form submission
  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault()

      const trimmedMessage = message.trim()

      // Validate message
      if (!trimmedMessage || disabled) {
        return
      }

      // Check rate limit
      if (!rateLimiter.current()) {
        setShowError(true)
        setTimeout(() => setShowError(false), 3000)
        return
      }

      // Send message
      onSend(trimmedMessage)
      setMessage('')

      // Stop typing indicator
      if (onTyping) {
        onTyping(false)
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }
      }

      // Keep focus on input
      inputRef.current?.focus()
    },
    [message, disabled, onSend, onTyping],
  )

  // Handle key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Submit on Enter (unless composing or Shift is held)
      if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [isComposing, handleSubmit],
  )

  // Handle composition events (for IME input)
  const handleCompositionStart = useCallback(() => {
    setIsComposing(true)
  }, [])

  const handleCompositionEnd = useCallback(() => {
    setIsComposing(false)
  }, [])

  const isDisabled = disabled || message.trim().length === 0

  return (
    <form className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-xl tw-pl-1 tw-text-sm ${className}`} onSubmit={handleSubmit}>
      <input
        ref={inputRef}
        type='text'
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className='tw-h-9 tw-flex-1 tw-border-none tw-bg-transparent tw-pl-2 tw-text-gray-800 tw-outline-none placeholder:tw-text-gray-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-50'
        aria-label='Chat message input'
      />

      {/* Send button */}
      <button
        type='submit'
        disabled={isDisabled}
        className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-border-none tw-text-lg tw-transition-all tw-duration-200 ${
          isDisabled ? 'tw-cursor-not-allowed tw-bg-gray-200 tw-text-gray-400' : 'tw-cursor-pointer tw-bg-blue-100 tw-text-blue-600 active:tw-scale-95 hover:tw-scale-105'
        }`}
        aria-label='Send message'
      >
        ➤
      </button>

      {/* Error message */}
      {showError && (
        <div className='tw-absolute tw-bottom-full tw-left-2 tw-mb-1 tw-animate-fade-in tw-whitespace-nowrap tw-rounded-lg tw-bg-red-50 tw-px-2 tw-py-1 tw-text-xs tw-text-red-600'>
          Too many messages. Please slow down.
        </div>
      )}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </form>
  )
}

// Simplified chat input for inline use
export const InlineChatInput: React.FC<{
  onSend: (message: string) => void
  disabled?: boolean
}> = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('')

  const handleSubmit = () => {
    if (message.trim() && !disabled) {
      onSend(message.trim())
      setMessage('')
    }
  }

  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      <input
        type='text'
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        placeholder='Type...'
        disabled={disabled}
        style={{
          flex: 1,
          padding: '4px 8px',
          fontSize: '12px',
          border: '1px solid #D1D5DB',
          borderRadius: '4px',
          outline: 'none',
        }}
      />
      <button
        onClick={handleSubmit}
        disabled={!message.trim() || disabled}
        style={{
          padding: '4px 8px',
          fontSize: '12px',
          backgroundColor: message.trim() && !disabled ? '#7C3AED' : '#E5E7EB',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '4px',
          cursor: message.trim() && !disabled ? 'pointer' : 'not-allowed',
        }}
      >
        Send
      </button>
    </div>
  )
}

export default ChatInput
